import { useEffect, useMemo, useRef, useState } from "react";
import { LiveMediaHelper } from "@kaltura/mediaspace-shared-utils";
import Lobby from "../lobby/Lobby";
import { CommonHeroLayout } from "../layout/CommonHeroLayout";
import { HeroProps } from "../HeroProps";
import { useKalturaServerTime } from "@kaltura/mediaspace-shared-hooks";
import { Player } from "@kaltura/mediaspace-shared-player";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config";
import { KalturaLiveEntry } from "kaltura-typescript-client/api/types/KalturaLiveEntry";
import { getPropsToPass, useThumbnailSizing } from "../helpers";
import { MediaLobbyDataContainer } from "../lobby/lobby-data/LobbyData";
import { useCncReactions } from "@mediaspace/features/cnc";

export interface KwebcastHeroProps extends Omit<HeroProps, "entry"> {
    removePlayerOnEnd?: boolean /** should the player be hidden when broadcast is done (show lobby instead) */;
    entry: KalturaLiveEntry;
}

/**
 * wrapper component for kwebcast media
 */
const KwebcastHero = (props: KwebcastHeroProps) => {
    const {
        schedulingData,
        playerConfig,
        entry,
        removePlayerOnEnd = false,
        actions = [],
        thumbnailUrl,
        isReadOnly,
    } = props;

    const config = useConfig();
    const serviceUrl = config?.application?.serviceUrl;
    const STREAM_POLL_INTERVAL = 5000;
    const [player, setPlayer] = useState<any>(null);
    const [showLobby, setShowLobby] = useState(true);
    const pollingInterval = useRef(0);
    const media = useMemo(() => {
        return { entryId: entry.id, ks: playerConfig.ks as string };
    }, [entry, playerConfig]);

    const { startReactions, endReactions } = useCncReactions();

    // tslint:disable-next-line:no-any
    const setupPlayerListeners = (playerInstance: any) => {
        playerInstance.addEventListener(playerInstance.Event.Core.PLAYER_DESTROY, teardownPlayerListeners);
        playerInstance.addEventListener(playerInstance.Event.Core.PLAYBACK_ENDED, handlePlaybackEnd);
        setPlayer(playerInstance);
    };

    // tslint:disable-next-line:no-any
    const teardownPlayerListeners = (event: any) => {
        // remove listeners
        if (!player) {
            return;
        }

        player.removeEventListener(player.Event.Core.PLAYER_DESTROY, teardownPlayerListeners);

        player.removeEventListener(player.Event.Core.PLAYBACK_ENDED, handlePlaybackEnd);
    };

    // tslint:disable-next-line:no-any
    const handlePlaybackEnd = (event: any) => {
        if (removePlayerOnEnd) {
            setShowLobby(true);
        }
        endReactions();
    };

    // set the kaltura server url
    useKalturaServerTime(serviceUrl);

    // get time from server, then init accordingly
    useEffect(() => {
        const pollStatus = () => {
            console.debug(">> polling primary stream... ");
            return LiveMediaHelper
                .pollKmsIsLive(entry.id)
                .then((value) => {
                    if (value) {
                        console.debug(">> entry is alive");
                        handleStreamStart();
                        return Promise.resolve(true);
                    }
                    else {
                        console.debug(">> entry is dead");
                        setShowLobby(true);
                        return Promise.resolve(false);
                    }
                })
                .catch((err) => {
                    console.debug(">> entry is dead");
                    console.error(err);
                    setShowLobby(true);
                    return Promise.resolve(false);
                });
        };

        const handleStreamStart = () => {
            // if live, show player
            setShowLobby(false);

            startReactions();
        };

        /**
         * logic of what to show when (player vs lobby), expected to only run once
         */
        const decideWhatToDo = () => {
            // check islive once, if live - show the live stream (done inside pollStatus()).
            pollStatus().then((result) => {
                if (!result) {
                    if (entry.recordedEntryId) {
                        // if entry is recorded, show recording
                        console.debug(">> found a recording");
                        setShowLobby(false);
                    }
                    else {
                        // otherwise, keep polling
                        window.clearInterval(pollingInterval.current);
                        pollingInterval.current = window.setInterval(
                            () => {
                                pollStatus().then((isLive) => {
                                    if (isLive) {
                                        // no need to keep polling if stream is live
                                        window.clearInterval(pollingInterval.current);
                                    }
                                });
                            },
                            STREAM_POLL_INTERVAL
                        );
                    }
                }
            });
        };
        decideWhatToDo();
        return () => {
            window.clearInterval(pollingInterval.current);
        };
    }, [entry.recordedEntryId, entry.id, schedulingData?.from, schedulingData?.to]);

    const { statusLabel, actionsToPass } = getPropsToPass(entry, schedulingData, actions, isReadOnly);

    const sizedThumbnailUrl = useThumbnailSizing(thumbnailUrl);

    return (
        <CommonHeroLayout collapsedHero={props.collapse}>
            {showLobby && (
                <Lobby
                    thumbnailUrl={sizedThumbnailUrl}
                    statusLabel={statusLabel}
                    title={entry.name}
                    schedulingData={schedulingData}
                    actions={actionsToPass}
                    data={<MediaLobbyDataContainer {...props} />}
                />
            )}
            {!showLobby && (
                <Player
                    media={media}
                    config={playerConfig}
                    className="kms-ds-media-page-player"
                    onReady={setupPlayerListeners}
                />
            )}
        </CommonHeroLayout>
    );
};

export default KwebcastHero;
